import { FormEvent, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import api, { Lane, VinInventoryItem } from '../lib/api'
import PremiumHeader from '../components/PremiumHeader'

export default function VinInventoryPage() {
  const [items, setItems] = useState<VinInventoryItem[]>([])
  const [customers, setCustomers] = useState<Array<{ id: string; name: string }>>([])
  const [lanes, setLanes] = useState<Lane[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [q, setQ] = useState('')
  const [customerId, setCustomerId] = useState('')
  const [laneId, setLaneId] = useState('')
  const [status, setStatus] = useState('')
  const [decodeStatus, setDecodeStatus] = useState('')

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.vinInventory.list({
        q: q || undefined,
        customerId: customerId || undefined,
        laneId: laneId || undefined,
        status: status || undefined,
        decodeStatus: decodeStatus || undefined,
      })
      setItems(response.data)
      setSelected([])
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to load VIN inventory')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const hydrate = async () => {
      try {
        const [customerRes, laneRes] = await Promise.all([
          api.customers.list(),
          api.lanes.list({ status: 'ACTIVE' }),
        ])
        setCustomers(customerRes.data.map((item) => ({ id: item.id, name: item.name })))
        setLanes(laneRes.data)
      } catch {
        // Lookup loading is best-effort.
      }
    }
    void hydrate()
    void load()
  }, [])

  const onSearch = (event: FormEvent) => {
    event.preventDefault()
    void load()
  }

  const allSelected = items.length > 0 && selected.length === items.length
  const selectedItems = useMemo(() => {
    const selectedSet = new Set(selected)
    return items.filter((item) => selectedSet.has(item.id))
  }, [items, selected])

  const bulk = async (action: 'decode' | 'archive' | 'assign_lane' | 'assign_customer') => {
    if (!selected.length) return
    if (action === 'assign_lane' && !laneId) {
      setError('Select lane filter/value before assign lane bulk action.')
      return
    }
    if (action === 'assign_customer' && !customerId) {
      setError('Select customer filter/value before assign customer bulk action.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await api.vinInventory.bulkAction({
        itemIds: selected,
        action,
        laneId: action === 'assign_lane' ? laneId : undefined,
        customerId: action === 'assign_customer' ? customerId : undefined,
      })
      await load()
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Bulk action failed')
    } finally {
      setSaving(false)
    }
  }

  const convertSelectedToOrder = async () => {
    if (!selected.length) return
    setSaving(true)
    setError(null)
    try {
      const response = await api.vinInventory.convertToOrder({
        itemIds: selected,
        customerId: customerId || undefined,
        laneId: laneId || undefined,
      })
      window.location.href = `/orders/${response.data.order.id}`
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to convert selected items to order')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-4">
      <PremiumHeader
        title="VIN Inventory"
        subtitle="Import dealer batches, decode vehicles, and convert inventory into moves."
        primaryAction={
          <Link to="/vin-inventory/import" className="bg-blue-700 text-white rounded px-3 py-2 text-sm">Bulk Import</Link>
        }
        secondaryAction={
          <Link to="/vin-batches" className="border rounded px-3 py-2 text-sm">Batches</Link>
        }
      />

      {error && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <form className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-6 gap-2" onSubmit={onSearch}>
        <input
          className="md:col-span-2 border rounded px-3 py-2 text-sm"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search VIN / last8 / make / model / batch"
        />

        <select className="border rounded px-3 py-2 text-sm" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
          <option value="">Customer</option>
          {customers.map((customer) => (
            <option key={customer.id} value={customer.id}>{customer.name}</option>
          ))}
        </select>

        <select className="border rounded px-3 py-2 text-sm" value={laneId} onChange={(e) => setLaneId(e.target.value)}>
          <option value="">Lane</option>
          {lanes.map((lane) => (
            <option key={lane.id} value={lane.id}>{lane.laneNumber}</option>
          ))}
        </select>

        <select className="border rounded px-3 py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Status</option>
          <option value="AVAILABLE">AVAILABLE</option>
          <option value="SELECTED">SELECTED</option>
          <option value="QUOTED">QUOTED</option>
          <option value="ORDERED">ORDERED</option>
          <option value="LOADED">LOADED</option>
          <option value="MOVED">MOVED</option>
          <option value="ARCHIVED">ARCHIVED</option>
        </select>

        <select className="border rounded px-3 py-2 text-sm" value={decodeStatus} onChange={(e) => setDecodeStatus(e.target.value)}>
          <option value="">Decode</option>
          <option value="PENDING">PENDING</option>
          <option value="DECODED">DECODED</option>
          <option value="FAILED">FAILED</option>
          <option value="INVALID">INVALID</option>
        </select>

        <button className="border rounded px-3 py-2 text-sm">Apply Filters</button>
      </form>

      <div className="bg-white border rounded-xl p-3 flex flex-wrap items-center gap-2 text-sm">
        <button disabled={saving || !selected.length} onClick={() => void bulk('decode')} className="border rounded px-3 py-1">Bulk Decode</button>
        <button disabled={saving || !selected.length} onClick={() => void bulk('assign_lane')} className="border rounded px-3 py-1">Bulk Assign Lane</button>
        <button disabled={saving || !selected.length} onClick={() => void bulk('assign_customer')} className="border rounded px-3 py-1">Bulk Assign Customer</button>
        <button disabled={saving || !selected.length} onClick={convertSelectedToOrder} className="bg-blue-700 text-white rounded px-3 py-1">Convert to Order</button>
        <button disabled={saving || !selected.length} onClick={() => void bulk('archive')} className="border border-red-300 text-red-700 rounded px-3 py-1">Archive</button>
        <span className="text-gray-500">Selected {selected.length}</span>
      </div>

      {loading && <div className="text-sm text-gray-500">Loading VIN inventory...</div>}
      {!loading && !items.length && <div className="text-sm text-gray-500">No VIN inventory items found.</div>}

      {!loading && items.length > 0 && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => setSelected(allSelected ? [] : items.map((item) => item.id))}
                  />
                </th>
                <th className="px-3 py-2">VIN</th>
                <th className="px-3 py-2">Vehicle</th>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2">Lane</th>
                <th className="px-3 py-2">Route</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Decode</th>
                <th className="px-3 py-2">Order/Load</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-t">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.includes(item.id)}
                      onChange={() => setSelected((prev) => (prev.includes(item.id) ? prev.filter((id) => id !== item.id) : [...prev, item.id]))}
                    />
                  </td>
                  <td className="px-3 py-2 font-mono">{item.vin}</td>
                  <td className="px-3 py-2">{[item.year, item.make, item.model].filter(Boolean).join(' ') || '-'}</td>
                  <td className="px-3 py-2">{item.customerName || '-'}</td>
                  <td className="px-3 py-2">{item.laneNumber || '-'}</td>
                  <td className="px-3 py-2">{item.pickupZip || '-'} to {item.deliveryZip || '-'}</td>
                  <td className="px-3 py-2">{item.inventoryStatus}</td>
                  <td className="px-3 py-2">{item.decodeStatus}{item.decodeError ? ` (${item.decodeError})` : ''}</td>
                  <td className="px-3 py-2">
                    {item.orderId ? <Link to={`/orders/${item.orderId}`} className="text-blue-700 hover:underline">{item.orderNumber || 'Order'}</Link> : '-'}
                    {item.loadId ? <span> | <Link to={`/loads/${item.loadId}`} className="text-blue-700 hover:underline">{item.loadNumber || 'Load'}</Link></span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!!selectedItems.length && (
        <div className="text-xs text-gray-500">Selected VINs: {selectedItems.map((item) => item.vin).join(', ')}</div>
      )}
    </div>
  )
}
